import { useRef, useState } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { useDialogFocus } from "@hybrid-iptv/ui";
import type { ChannelHealth } from "../library/channelHealthStore.js";
import { useFavoritesStore } from "../library/favoritesStore.js";

interface ChannelStatusDialogProps {
  channels: Channel[];
  health: Record<string, ChannelHealth>;
  /** Test one channel now; true when it answered (and so is back). */
  onTryNow: (channel: Channel) => Promise<boolean>;
  /** Put a hidden channel back in the guide with a clean count. */
  onShowAnyway: (channel: Channel) => void;
  onClose: () => void;
}

/** Format the last-check ISO time as a compact local time, e.g. "9:14 PM". */
function formatTime(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}

/**
 * "Channel status" report view (technical-spec §8.3) — anything the health
 * system knows, the user can see: every channel that failed a try, with its
 * last check time. A channel that failed three separate tries is hidden from
 * the guide; this is where it surfaces instead of vanishing silently, with
 * "Try now" and "Show anyway" (owner, 2026-10-21: "hide it but don't lose it").
 */
export function ChannelStatusDialog({ channels, health, onTryNow, onShowAnyway, onClose }: ChannelStatusDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ open: true, onClose, rootRef: dialogRef });
  const isSystemHidden = useFavoritesStore((s) => s.isSystemHidden);
  // Subscribed so a row's words change the moment it is shown or comes back.
  useFavoritesStore((s) => s.hidden);
  const [trying, setTrying] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const dead = channels.filter((c) => health[c.id]?.state === "dead");
  const degraded = channels.filter((c) => health[c.id]?.state === "degraded");
  const rows = [...dead, ...degraded];

  // A row whose channel comes back loses its buttons: focus moves to the next row's, or to Close,
  // never to nothing (navigation rule: no press lands on a vanished control).
  const refocusFrom = (rowIndex: number) => {
    requestAnimationFrame(() => {
      const root = dialogRef.current;
      if (!root || root.contains(document.activeElement)) return;
      const rowsLeft = [...root.querySelectorAll<HTMLElement>(".status-dialog__row")];
      const next = rowsLeft.slice(rowIndex).concat(rowsLeft.slice(0, rowIndex)).map((row) => row.querySelector<HTMLElement>("button")).find(Boolean);
      (next ?? root.querySelector<HTMLElement>(".status-dialog__actions button"))?.focus();
    });
  };

  const tryNow = async (channel: Channel, rowIndex: number) => {
    if (trying) return;
    setTrying(channel.id);
    try {
      const ok = await onTryNow(channel);
      setAnswers((all) => ({ ...all, [channel.id]: ok ? "answered · back in the guide" : "still not answering" }));
      if (ok) refocusFrom(rowIndex);
    } finally {
      setTrying(null);
    }
  };

  return (
    <div className="status-dialog__overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="status-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Channel status"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="status-dialog__title">Channel status</h2>
        {rows.length === 0 ? (
          <p className="status-dialog__empty">
            No failing channels right now. A channel that fails three separate
            tries is hidden here, and tested again at every update.
          </p>
        ) : (
          <ul className="status-dialog__list">
            {rows.map((channel, rowIndex) => {
              const entry = health[channel.id]!;
              const hidden = isSystemHidden(channel.id);
              return (
                <li key={channel.id} className="status-dialog__row">
                  <span className={`epg-health-dot epg-health-dot--${entry.state}`} aria-hidden="true" />
                  <span className="status-dialog__name">{channel.name}</span>
                  <span className="status-dialog__meta">
                    {entry.state === "dead" ? "not responding" : `failed ${entry.failStreak} of 3 tries`}
                    {entry.lastStatus !== null ? ` · HTTP ${entry.lastStatus}` : ""}
                    {hidden ? " · hidden from guide" : ""}
                    {" · checked "}
                    {formatTime(entry.lastCheckedAt)}
                    {answers[channel.id] ? ` · ${answers[channel.id]}` : ""}
                  </span>
                  {hidden && (
                    <span className="status-dialog__row-actions">
                      <button type="button" className="ui-btn ui-btn--ghost" onClick={() => void tryNow(channel, rowIndex)}>
                        {trying === channel.id ? "Trying…" : "Try now"}
                      </button>
                      <button
                        type="button"
                        className="ui-btn ui-btn--ghost"
                        onClick={() => {
                          onShowAnyway(channel);
                          refocusFrom(rowIndex);
                        }}
                      >
                        Show anyway
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="status-dialog__actions">
          <button type="button" className="ui-btn ui-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
